import React, { useState, useEffect } from 'react';
import { GitBranch, Calendar, Clock, User, Star, GitFork } from 'lucide-react';
import GitHubService from '../services/github';
import toast from 'react-hot-toast';
import { GitHubIcon } from './icons/BrandIcons';

const GitHubIntegration = () => {
  const [repositories, setRepositories] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [commits, setCommits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      const status = await GitHubService.getConnectionStatus();
      setConnectionStatus(status);
      
      if (status.connected) {
        fetchRepositories();
      }
    } catch (error) {
      setConnectionStatus({ connected: false });
      toast.error('Could not check GitHub status. Are you logged in?');
    }
  };

  const fetchRepositories = async () => {
    try {
      setLoading(true);
      const repos = await GitHubService.getUserRepositories();
      setRepositories(repos);
      
      if (repos.length > 0) {
        setSelectedRepo(repos[0].full_name);
        await fetchCommits(repos[0].full_name);
      }
    } catch (error) {

      toast.error('Failed to fetch repositories: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCommits = async (repoName) => {
    if (!repoName) return;
    
    try {
      setLoading(true);
      const commitsData = await GitHubService.getRepositoryCommits(repoName);
      setCommits(commitsData);
    } catch (error) {

      toast.error('Failed to fetch commits: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleRepoChange = async (repoName) => {
    setSelectedRepo(repoName);
    await fetchCommits(repoName);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateMessage = (message, maxLength = 80) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  if (!connectionStatus) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!connectionStatus.connected) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center">
          <GitHubIcon className="w-9 h-9 text-secondary-900 dark:text-white" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          GitHub Not Connected
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Connect your GitHub account to view repositories and commits.
        </p>
        <button
          onClick={() => window.location.href = '/profile'}
          className="px-4 py-2 bg-secondary-900 text-white rounded-lg hover:bg-secondary-800 transition-colors inline-flex items-center gap-2"
        >
          <GitHubIcon className="w-4 h-4 text-white" />
          Go to Profile
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center">
            <GitHubIcon className="w-6 h-6 text-secondary-900 dark:text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              GitHub
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Connected as <span className="font-medium">{connectionStatus.account_login}</span>
            </p>
          </div>
        </div>
        <button
          onClick={checkConnectionStatus}
          disabled={loading}
          className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {/* Repository Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Repository
        </label>
        <select
          value={selectedRepo}
          onChange={(e) => handleRepoChange(e.target.value)}
          disabled={loading || repositories.length === 0}
          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {repositories.length === 0 ? (
            <option value="">No repositories available</option>
          ) : (
            repositories.map((repo) => (
              <option key={repo.id} value={repo.full_name}>
                {repo.source === 'organization' ? `🏢 ${repo.full_name}` : `👤 ${repo.full_name}`}
              </option>
            ))
          )}
        </select>
        {repositories.length > 0 && (
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">Found:</span> {repositories.filter(r => r.source === 'user').length} user repos, {repositories.filter(r => r.source === 'organization').length} organization repos
          </div>
        )}
      </div>

      {/* Repository Info */}
      {selectedRepo && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
            Repository Information
          </h3>
          {repositories.find(r => r.full_name === selectedRepo) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {repositories.find(r => r.full_name === selectedRepo)?.stargazers_count || 0} stars
                </span>
              </div>
              <div className="flex items-center gap-2">
                <GitFork className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {repositories.find(r => r.full_name === selectedRepo)?.forks_count || 0} forks
                </span>
              </div>
              <div className="flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-green-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {repositories.find(r => r.full_name === selectedRepo)?.language || 'Unknown'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {formatDate(repositories.find(r => r.full_name === selectedRepo)?.updated_at)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Commits */}
      {selectedRepo && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
            Recent Commits
          </h3>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : commits.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              No commits found for this repository.
            </p>
          ) : (
            <div className="space-y-3">
              {commits.slice(0, 10).map((commit) => (
                <div
                  key={commit.sha}
                  className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                        {truncateMessage(commit.message)}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {commit.author.name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(commit.author.date)}
                        </span>
                        <span className="font-mono text-xs">
                          {commit.sha.substring(0, 7)}
                        </span>
                      </div>
                    </div>
                    <a
                      href={commit.html_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 text-sm"
                    >
                      View
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GitHubIntegration;
